import { Injectable } from '@angular/core';
import { Label, LanguageService } from '@services/public';
import { Select, toVoidPromise } from '@util';
import { ChartConfiguration } from 'chart.js';
import { uuid } from 'jakapa-utilities-lite';
import { Observable } from 'rxjs';
import { LeaderUserService, User } from '../user.service';

export interface LeaderReportAPIResponseSelfVsPeerByUser {
	userUUID: uuid;
	self: number;
	peer: number;
}

@Injectable({
	providedIn: 'root',
})
export class LeaderReportSelfVsPeerByUserService {
	// Page langauge.
	labels: { [key in Label]?: string } = {
		[Label.PEER]: '',
		[Label.SELF]: '',
		[Label.SKILL_LEVEL]: '',
		[Label.UNKNOWN_USER]: '',
	};

	constructor(
		private _languageSvc: LanguageService,
		private _userSvc: LeaderUserService,
	) {
		// Get page language.
		this._languageSvc.getLabels(this.labels);
	}

	async generateReport(
		data: Array<LeaderReportAPIResponseSelfVsPeerByUser>,
		viewAllSelect: Select<boolean | null>,
	): Promise<ChartConfiguration | null> {
		// Check if viewAll needs to be toggled.
		if (viewAllSelect.value === null && data.length > 6) {
			viewAllSelect.value = false;
			return null;
		}
		if (viewAllSelect.value === false && data.length <= 6) {
			viewAllSelect.value = null;
			return null;
		}

		// Get users list.
		let users: Array<User> = [];
		const apiCall: Observable<Array<User>> = this._userSvc.getUsers();
		apiCall.subscribe((res: Array<User>) => (users = res));
		await toVoidPromise(apiCall);

		// Format report data.
		const labels: Array<string> = [];
		const selfData: Array<number> = [];
		const peerData: Array<number> = [];
		for (const userData of data) {
			labels.push(
				users.find((user) => user.uuid === userData.userUUID)?.name ||
					this.labels[Label.UNKNOWN_USER],
			);
			selfData.push(userData.self);
			peerData.push(userData.peer);
		}

		// Truncate data.
		if (viewAllSelect.value === false) {
			labels.splice(6, Infinity);
			selfData.splice(6, Infinity);
			peerData.splice(6, Infinity);
		}

		return {
			type: 'bar',
			data: {
				labels,
				datasets: [
					{
						label: this.labels[Label.SELF],
						data: selfData,
						borderRadius: 5,
						backgroundColor: '#3CE986',
					},
					{
						label: this.labels[Label.PEER],
						data: peerData,
						borderRadius: 5,
						backgroundColor: '#74A2FF',
					},
				],
			},
			options: {
				plugins: {
					legend: {
						display: true,
						position: 'top',
						align: 'end',
						labels: {
							useBorderRadius: true,
							borderRadius: 5.5,
							boxWidth: 10,
							boxHeight: 10,
						},
					},
				},
				responsive: true,
				maintainAspectRatio: false,
				indexAxis: 'y',
				scales: {
					y: {
						grid: {
							display: false,
						},
					},
					x: {
						min: 0,
						max: 5,
						ticks: {
							stepSize: 0.5,
						},
						title: {
							display: true,
							text: this.labels[Label.SKILL_LEVEL],
							color: '#757575',
						},
					},
				},
			},
		};
	}
}
